import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock } from "lucide-react";
import { useConfig } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useSeason } from "@/lib/holiday";

const left = (to) => {
  const ms = Math.max(0, new Date(to) - Date.now());
  return { ms, d: Math.floor(ms / 864e5), h: Math.floor(ms / 36e5) % 24, m: Math.floor(ms / 6e4) % 60, s: Math.floor(ms / 1e3) % 60 };
};

export const useCountdown = () => {
  const { data } = useConfig();
  const { season } = useSeason();
  const to = data?.xmas_last_order;
  const [v, setV] = useState(() => (to ? left(to) : null));
  useEffect(() => {
    if (!to) return;
    setV(left(to));
    const id = setInterval(() => setV(left(to)), 1000);
    return () => clearInterval(id);
  }, [to]);
  return season === "christmas" && v && v.ms > 0 ? { ...v, date: new Date(to) } : null;
};

const Unit = ({ n, label }) => (
  <div className="text-center min-w-[54px]">
    <motion.p key={n} initial={{ y: -10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="font-display text-3xl lg:text-4xl text-white tabular-nums">{String(n).padStart(2, "0")}</motion.p>
    <p className="text-[10px] uppercase tracking-[0.12em] text-white/50">{label}</p>
  </div>
);

export const Countdown = ({ compact }) => {
  const { t, lang } = useI18n();
  const c = useCountdown();
  if (!c) return null;
  const date = c.date.toLocaleDateString(lang === "no" ? "nb" : lang, { day: "numeric", month: "long" });
  if (compact) {
    return (
      <p data-testid="countdown-compact" className="flex items-center gap-2 text-xs text-[#e3cb94] bg-[#c8102e]/20 border border-[#c8102e]/40 px-3 py-2">
        <Clock size={13} /> {t("countdown_text")}: {date} · {c.d}{t("countdown_days").charAt(0)} {c.h}h {c.m}m
      </p>
    );
  }
  return (
    <div data-testid="christmas-countdown" className="inline-flex flex-col gap-3 border border-[#c9a55c]/40 bg-[#0b0a08]/70 backdrop-blur-md px-5 py-4">
      <p className="eyebrow flex items-center gap-2"><Clock size={13} /> {t("countdown_label")} · {date}</p>
      <div className="flex items-center gap-2">
        <Unit n={c.d} label={t("countdown_days")} /><span className="text-gold text-2xl">:</span>
        <Unit n={c.h} label={t("countdown_hours")} /><span className="text-gold text-2xl">:</span>
        <Unit n={c.m} label={t("countdown_minutes")} /><span className="text-gold text-2xl">:</span>
        <Unit n={c.s} label={t("countdown_seconds")} />
      </div>
    </div>
  );
};
